import { useCallback, useEffect, useState } from 'react';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import MatrixRainBackground from '../components/auth/MatrixRainBackground';
import { useAuth } from '../context/AuthContext';

function validateLoginFields(email, password) {
  return Boolean(email && password);
}

function getRedirectPath(location) {
  return location.state?.from || '/dashboard';
}

function useLoginForm(login, location, navigate) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (!validateLoginFields(email, password)) {
      setErrorMessage('Veuillez renseigner votre email et votre mot de passe.');
      return;
    }

    setIsSubmitting(true);
    const result = await login(email.trim(), password);
    if (result.ok) {
      navigate(getRedirectPath(location), { replace: true });
    } else {
      setErrorMessage(result.error || 'Email ou mot de passe incorrect.');
    }
    setIsSubmitting(false);
  }, [email, location, login, navigate, password]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((current) => !current);
  }, []);

  return {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    togglePasswordVisibility,
    isSubmitting,
    errorMessage,
    handleSubmit,
  };
}

function LoginEmailField({ email, setEmail }) {
  return (
    <div className="relative">
      <Mail className="pointer-events-none absolute left-4 top-4 h-6 w-6 text-zinc-500" />
      <Input
        data-testid="email-input"
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        placeholder="Votre adresse email"
        autoComplete="email"
        className="h-14 border-zinc-800 bg-zinc-900/50 pl-12 text-white placeholder:text-zinc-600 focus-visible:border-white focus-visible:ring-0"
      />
    </div>
  );
}

function LoginPasswordField({ password, setPassword, showPassword, onTogglePassword }) {
  return (
    <div className="relative">
      <Lock className="pointer-events-none absolute left-4 top-4 h-6 w-6 text-zinc-500" />
      <Input
        data-testid="password-input"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        placeholder="Votre mot de passe"
        autoComplete="current-password"
        className="h-14 border-zinc-800 bg-zinc-900/50 pl-12 pr-12 text-white placeholder:text-zinc-600 focus-visible:border-white focus-visible:ring-0"
      />
      <button
        type="button"
        data-testid="toggle-password-visibility-button"
        aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
        onClick={onTogglePassword}
        className="absolute right-4 top-4 text-zinc-500 transition-colors hover:text-white"
      >
        {showPassword ? <EyeOff className="h-6 w-6" /> : <Eye className="h-6 w-6" />}
      </button>
    </div>
  );
}

function LoginErrorMessage({ errorMessage }) {
  if (!errorMessage) {
    return null;
  }

  return <p data-testid="login-error-message" className="text-sm text-rose-400">{errorMessage}</p>;
}

function LoginSubmitButton({ isSubmitting, isCheckingAuth }) {
  return (
    <Button data-testid="login-button" type="submit" disabled={isSubmitting || isCheckingAuth} className="h-14 w-full rounded-sm bg-white text-base font-semibold text-black transition-transform duration-200 hover:scale-[1.01] hover:bg-zinc-200">
      {isSubmitting ? 'Connexion...' : 'Se connecter'}
    </Button>
  );
}

function LoginCard({ formState, isCheckingAuth }) {
  return (
    <div className="login-card w-full rounded-lg p-8">
      <div className="mb-8 text-center">
        <h1 data-testid="login-title" className="font-['Playfair_Display'] text-3xl leading-tight text-white sm:text-4xl">
          ChatBot
        </h1>
      </div>
      <form onSubmit={formState.handleSubmit} className="space-y-4" data-testid="login-form">
        <LoginEmailField email={formState.email} setEmail={formState.setEmail} />
        <LoginPasswordField
          password={formState.password}
          setPassword={formState.setPassword}
          showPassword={formState.showPassword}
          onTogglePassword={formState.togglePasswordVisibility}
        />
        <LoginErrorMessage errorMessage={formState.errorMessage} />
        <LoginSubmitButton isSubmitting={formState.isSubmitting} isCheckingAuth={isCheckingAuth} />
        <div className="text-center">
          <Link data-testid="forgot-password-link" to="/forgot-password" className="text-sm text-zinc-400 transition-colors duration-200 hover:text-white">
            Mot de passe oublié ?
          </Link>
        </div>
      </form>
    </div>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isCheckingAuth, login } = useAuth();
  const formState = useLoginForm(login, location, navigate);

  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate, user]);

  return (
    <div className="login-page-bg login-matrix-shell flex min-h-screen items-center justify-center px-4 py-8" data-testid="login-page-container">
      <MatrixRainBackground />
      <div className="login-page-content w-full max-w-sm">
        <div className="mb-10 text-center">
          <img data-testid="login-logo" src="/logo-braux-white.png" alt="Braux Paysagistes" className="mx-auto h-20 w-auto" />
        </div>
        <LoginCard formState={formState} isCheckingAuth={isCheckingAuth} />
      </div>
    </div>
  );
}
